import React from 'react';
import { Input, Form, Row, Switch, Select, InputNumber } from 'antd';
import { FormItemWrap } from 'react-structure-admin';
import ResCol from 'components/forms/ResCol';
import {
  InputNumeric,
  InputPercent,
  SelectSpecialTaxRegime,
  SelectTaxRegime
} from 'components';
import SearchValue from 'components/SearchValue';
import usePermissions from 'core/hooks/usePermissions';

const CompanySettingsForm = ({
  data,
  enabled,
  hideAllowToCreateOtherCompanies
}) => {
  const { isFinancialAdministrator } = usePermissions();
  const { serviceInvoice } = data ?? {};

  const useBatchNumber = false;

  return (
    <>
      <Row>
        <ResCol span={6}>
          <FormItemWrap
            name={['settings', 'serviceInvoice', 'enabled']}
            label="Habilitar emissão?"
            valuePropName="checked"
          >
            <Switch />
          </FormItemWrap>
        </ResCol>
        <ResCol span={6}>
          <FormItemWrap
            name={['settings', 'serviceInvoice', 'environmentType']}
            label="Ambiente"
            required={enabled}
          >
            <Select disabled={!enabled}>
              <Select.Option value="production">Produção</Select.Option>
              <Select.Option value="homologation">Homologação</Select.Option>
            </Select>
          </FormItemWrap>
        </ResCol>
        <ResCol span={6}>
          <FormItemWrap
            label="CRT"
            name={['settings', 'serviceInvoice', 'taxRegime']}
            required={enabled}
          >
            <SelectTaxRegime
              dropdownMatchSelectWidth={false}
              disabled={!enabled}
            />
          </FormItemWrap>
        </ResCol>
        <ResCol span={6}>
          <FormItemWrap
            label="Regime Especial"
            name={['settings', 'serviceInvoice', 'specialTaxRegime']}
            required={enabled}
          >
            <SelectSpecialTaxRegime
              dropdownMatchSelectWidth={false}
              disabled={!enabled}
            />
          </FormItemWrap>
        </ResCol>
      </Row>

      <Row>
        <ResCol span={useBatchNumber ? 8 : 12}>
          {serviceInvoice?.provider !== 'softplan' &&
            (serviceInvoice?.useInvoiceNumber || serviceInvoice?.useRps) && (
              <FormItemWrap
                name={['settings', 'serviceInvoice', 'nextNumber']}
                label={
                  data.serviceInvoice?.useRps
                    ? 'Número do próximo RPS'
                    : 'Número da próxima NFS-e'
                }
              >
                <InputNumber
                  style={{ width: '100%' }}
                  max={99999999999}
                  disabled={!enabled}
                />
              </FormItemWrap>
            )}

          {serviceInvoice === 'softplan' ? (
            <FormItemWrap
              name={['settings', 'serviceInvoice', 'authNumber']}
              label="AEDF-e"
            >
              <InputNumeric
                style={{ width: '100%' }}
                maxLength={11}
                disabled={!enabled}
              />
            </FormItemWrap>
          ) : null}
        </ResCol>

        {serviceInvoice?.useRps && (
          <>
            {useBatchNumber && (
              <ResCol span={8}>
                <FormItemWrap
                  name={['settings', 'serviceInvoice', 'nextBatchNumber']}
                  label="Número do próximo Lote"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={99999999999}
                    disabled={!enabled}
                  />
                </FormItemWrap>
              </ResCol>
            )}
            {serviceInvoice?.useSeries && (
              <ResCol span={useBatchNumber ? 8 : 12}>
                <FormItemWrap
                  name={['settings', 'serviceInvoice', 'series']}
                  label="Série do RPS"
                >
                  <Input maxLength={10} disabled={!enabled} />
                </FormItemWrap>
              </ResCol>
            )}
          </>
        )}
        {serviceInvoice?.authentication === 'userName' && (
          <>
            <ResCol span={8}>
              <FormItemWrap
                label="Usuário"
                name={['settings', 'serviceInvoice', 'userName']}
                required
              >
                <Input maxLength={100} disabled={!enabled} />
              </FormItemWrap>
            </ResCol>
            <ResCol span={8}>
              <FormItemWrap
                label="Senha"
                name={['settings', 'serviceInvoice', 'password']}
                required
              >
                <Input maxLength={100} type="password" disabled={!enabled} />
              </FormItemWrap>
            </ResCol>
          </>
        )}
        {serviceInvoice?.authentication === 'token' && (
          <>
            <ResCol
              span={!serviceInvoice?.useSeries && !useBatchNumber ? 16 : 16}
            >
              <FormItemWrap
                label="Token/Senha"
                name={['settings', 'serviceInvoice', 'password']}
              >
                <Input maxLength={100} type="password" disabled={!enabled} />
              </FormItemWrap>
            </ResCol>
          </>
        )}
        <Form.Item name={['settings', 'serviceInvoice', 'useRps']} hidden />
        <Form.Item
          name={['settings', 'serviceInvoice', 'useBatchNumber']}
          hidden
        />
        <Form.Item name={['settings', 'serviceInvoice', 'useSeries']} hidden />
        <Form.Item
          name={['settings', 'serviceInvoice', 'useFederalServiceCode']}
          hidden
        />
      </Row>

      {serviceInvoice === 'sigep' ? (
        <ResCol span={12}>
          <FormItemWrap
            name={['settings', 'serviceInvoice', 'authNumber']}
            label="Chave privada"
          >
            <Input maxLength={40} type="password" disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
      ) : null}

      <Row>
        <ResCol span={serviceInvoice?.useFederalServiceCode ? 12 : 24}>
          <FormItemWrap
            label="Natureza da Operação"
            name={['settings', 'serviceInvoice', 'taxationType']}
          >
            <Select disabled={!enabled}>
              <Select.Option value="taxationInMunicipality">
                Tributado no Município
              </Select.Option>
              <Select.Option value="taxationOutsideMunicipality">
                Tributado em outro município
              </Select.Option>
              <Select.Option value="exemption">Isento</Select.Option>
              <Select.Option value="suspendedByCourt">
                Suspenso por Decisão Judicial
              </Select.Option>
              <Select.Option value="suspendedByAdministrativeProcedure">
                Suspenço por Decisão Administrativa
              </Select.Option>
            </Select>
          </FormItemWrap>
        </ResCol>
        {serviceInvoice?.useFederalServiceCode && (
          <ResCol span={12}>
            <FormItemWrap
              label="Item da Lista de Serviço (LC 116/03)"
              name={['settings', 'serviceInvoice', 'federalServiceCode']}
            >
              <SearchValue
                placeholder="Selecione um item"
                drawerSize="50%"
                title="Selecione o item da lista de serviço"
                resource="federal-services"
                disabled={!enabled}
                columns={[
                  {
                    title: 'Código',
                    dataIndex: ['code'],
                    isKey: true
                  },
                  {
                    title: 'Descrição',
                    dataIndex: ['description']
                  }
                ]}
              />
            </FormItemWrap>
          </ResCol>
        )}
      </Row>

      <Row>
        <ResCol span={12}>
          <FormItemWrap
            label={
              serviceInvoice === 'softplan'
                ? 'CST'
                : 'Código do serviço no munícipio'
            }
            name={['settings', 'serviceInvoice', 'cityServiceCode']}
          >
            <Input maxLength={100} disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
        <ResCol span={12}>
          <FormItemWrap
            label="CNAE"
            name={['settings', 'serviceInvoice', 'cnaeCode']}
          >
            <SearchValue
              placeholder="Selecione um CNAE"
              drawerSize="50%"
              title="Selecione o CNAE"
              resource="cnaes"
              disabled={!enabled}
              columns={[
                {
                  title: 'Código',
                  dataIndex: 'code',
                  isKey: true,
                  width: '25%'
                },
                {
                  title: 'Descrição',
                  dataIndex: 'description',
                  width: '75%'
                }
              ]}
            />
          </FormItemWrap>
        </ResCol>
      </Row>
      <Row>
        <ResCol span={4}>
          <FormItemWrap
            label="ISS"
            name={['settings', 'serviceInvoice', 'issRate']}
          >
            <InputPercent disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
        <ResCol span={4}>
          <FormItemWrap
            label="PIS"
            name={['settings', 'serviceInvoice', 'pisRate']}
          >
            <InputPercent disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
        <ResCol span={4}>
          <FormItemWrap
            label="COFINS"
            name={['settings', 'serviceInvoice', 'cofinsRate']}
          >
            <InputPercent disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
        <ResCol span={4}>
          <FormItemWrap
            label="IR"
            name={['settings', 'serviceInvoice', 'irRate']}
          >
            <InputPercent disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
        <ResCol span={4}>
          <FormItemWrap
            label="INSS"
            name={['settings', 'serviceInvoice', 'inssRate']}
          >
            <InputPercent disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
        <ResCol span={4}>
          <FormItemWrap
            label="CSLL"
            name={['settings', 'serviceInvoice', 'csllRate']}
          >
            <InputPercent disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
      </Row>
      <Row>
        <ResCol span={4}>
          <FormItemWrap
            name={['settings', 'serviceInvoice', 'issWithheld']}
            label="Reter ISS?"
            valuePropName="checked"
          >
            <Switch disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
        <ResCol span={4}>
          <FormItemWrap
            name={['settings', 'serviceInvoice', 'pisWithheld']}
            label="Reter PIS?"
            valuePropName="checked"
          >
            <Switch disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
        <ResCol span={4}>
          <FormItemWrap
            name={['settings', 'serviceInvoice', 'cofinsWithheld']}
            label="Reter COFINS?"
            valuePropName="checked"
          >
            <Switch disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
        <ResCol span={4}>
          <FormItemWrap
            name={['settings', 'serviceInvoice', 'irWithheld']}
            label="Reter IR?"
            valuePropName="checked"
          >
            <Switch disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
        <ResCol span={4}>
          <FormItemWrap
            name={['settings', 'serviceInvoice', 'inssWithheld']}
            labelAlign="center"
            label="Reter INSS?"
            valuePropName="checked"
          >
            <Switch disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
        <ResCol span={4}>
          <FormItemWrap
            name={['settings', 'serviceInvoice', 'csllWithheld']}
            label="Reter CSLL?"
            valuePropName="checked"
          >
            <Switch disabled={!enabled} />
          </FormItemWrap>
        </ResCol>
      </Row>
      {!isFinancialAdministrator && (
        <FormItemWrap
          name={['settings', 'allowToCreateOtherCompanies']}
          label="Habilitar a emissão de NFS-e por profissional?"
          valuePropName="checked"
          hidden={hideAllowToCreateOtherCompanies}
        >
          <Switch />
        </FormItemWrap>
      )}
    </>
  );
};

export default CompanySettingsForm;
